import { useAtomValue } from 'jotai';
import { startCase } from 'lodash';

import { useTranslate } from '@i18n';
import { cameraSetupAtom, floorSettingsAtom, uiNodeBySmpIdAtom } from '@store';
import { Box, Button, colors, GoalIcon, OutlinedButton, Text, ToggleSelectButton, u } from '@ui';

import { useMergedCorridors } from './useMergedCorridors';

type LiveAnalysisSettingsProps = {
    onSetupToggle: () => void;
    onEventToggle: () => void;
    setupParameters: number[];
    eventParameters: number[];
};

export const LiveAnalysisSettings = ({
    onSetupToggle,
    onEventToggle,
    setupParameters,
    eventParameters,
}: LiveAnalysisSettingsProps) => {
    const cameraSetup = useAtomValue(cameraSetupAtom);

    const parametersMap = useAtomValue(uiNodeBySmpIdAtom);

    const t = useTranslate();

    const floorSettings = useAtomValue(floorSettingsAtom);
    const cameraAngle = floorSettings?.cameraAngle;

    const corridorsMap = useMergedCorridors();
    return (
        <Box display="flex" flexDirection="column" gap={48} alignItems="stretch">
            <Box display="flex" flexDirection="column" gap={20}>
                <Text variant="h3">Goal Parameters</Text>
                <Box gap={20} display="flex">
                    <Box
                        style={{ backgroundColor: colors.gray[100], minHeight: 172, borderRadius: u(1), padding: u(2) }}
                        flex={1}
                        display="flex"
                        flexDirection="column"
                        gap={u(0.5)}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="center" gap={u(0.5)}>
                            <Text variant="h3">P1</Text>
                            {!!setupParameters.length && (
                                <Button onClick={onSetupToggle} size="small" rounded variant="secondary">
                                    Edit
                                </Button>
                            )}
                        </Box>
                        {setupParameters.length ? (
                            <Box flex={1} display="flex" flexDirection="column" gap={u(1)}>
                                {setupParameters.map((parameterId) => {
                                    const parameter = parametersMap[parameterId];
                                    if (!parameter) {
                                        return null;
                                    }
                                    return (
                                        <Box
                                            key={parameterId}
                                            style={{ backgroundColor: colors.white, borderRadius: u(1), padding: u(1) }}
                                        >
                                            <Box display="flex" flexDirection="column" gap={u(0.5)}>
                                                <Text variant="h3">{t(parameter.i18n ?? '-')}</Text>
                                                <Box
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                    style={{
                                                        borderRadius: u(0.5),
                                                        borderWidth: 1,
                                                        borderColor: colors.bluegray[300],
                                                        padding: u(0.5),
                                                    }}
                                                >
                                                    <Text variant="h3">{corridorsMap[parameterId]?.min}</Text>
                                                    <GoalIcon></GoalIcon>
                                                    <Text variant="h3">{corridorsMap[parameterId]?.max}</Text>
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        ) : (
                            <Box
                                flex={1}
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                            >
                                <OutlinedButton
                                    isActive
                                    variant="secondary"
                                    icon={<GoalIcon />}
                                    onClick={onSetupToggle}
                                >
                                    Select Goal Parameters
                                </OutlinedButton>
                            </Box>
                        )}
                    </Box>
                    <Box
                        style={{ backgroundColor: colors.gray[100], borderRadius: u(1), padding: u(2) }}
                        flex={1}
                        display={'flex'}
                        flexDirection={'column'}
                        gap={u(0.5)}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="center" gap={u(0.5)}>
                            <Text variant="h3">{`P2-8 (Optional)`}</Text>
                            {!!eventParameters.length && (
                                <Button onClick={onEventToggle} size="small" rounded variant="secondary">
                                    Edit
                                </Button>
                            )}
                        </Box>
                        {eventParameters.length ? (
                            <Box flex={1} display="flex" flexDirection="column" gap={u(1)}>
                                {eventParameters.map((parameterId) => {
                                    const parameter = parametersMap[parameterId];
                                    if (!parameter) {
                                        return null;
                                    }
                                    return (
                                        <Box
                                            key={parameterId}
                                            style={{ backgroundColor: colors.white, borderRadius: u(1), padding: u(1) }}
                                        >
                                            <Box display="flex" flexDirection="column" gap={u(0.5)}>
                                                <Text variant="h3">{t(parameter.i18n ?? '-')}</Text>
                                                <Box
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                    style={{
                                                        borderRadius: u(0.5),
                                                        borderWidth: 1,
                                                        borderColor: colors.bluegray[300],
                                                        padding: u(0.5),
                                                    }}
                                                >
                                                    <Text variant="h3">{corridorsMap[parameterId]?.min}</Text>
                                                    <GoalIcon></GoalIcon>
                                                    <Text variant="h3">{corridorsMap[parameterId]?.max}</Text>
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        ) : (
                            <Box
                                flex={1}
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                            >
                                <OutlinedButton
                                    isActive
                                    variant="secondary"
                                    icon={<GoalIcon />}
                                    onClick={onEventToggle}
                                >
                                    Select Goal Parameters
                                </OutlinedButton>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
            <Box display="flex" flexDirection="column" gap={20}>
                <Text variant="h3">Floor Screen Layout</Text>
                <Box gap={20} display="flex">
                    <Box
                        style={{ backgroundColor: colors.gray[100], height: 344, borderRadius: u(1), padding: u(2) }}
                        flex={1}
                        display="flex"
                        flexDirection="column"
                    >
                        <Text variant="h3">P1</Text>
                        <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                            <ToggleSelectButton
                                options={cameraSetup?.cameras.map((c) => startCase(c.name)) ?? []}
                                prefix="Selected Camera*"
                                variant="outlined"
                                selectedOption={startCase(cameraAngle ?? '')}
                                isDisabled
                            />
                        </Box>
                        <Text variant="small">
                            *Camera selection will be added in upcoming version. Use User Settings to select a different
                            camera.
                        </Text>
                    </Box>
                    {/* TODO: add camera controls */}
                    {/* <Box
                        style={{ backgroundColor: colors.gray[100], height: 344, borderRadius: u(1), padding: u(2) }}
                        display="flex"
                        flexDirection="column"
                    >
                        <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                            <IconButton>
                                <PlusIcon size={24} />
                            </IconButton>
                        </Box>
                    </Box> */}
                </Box>
            </Box>
        </Box>
    );
};
